import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Row, Col, Table, Button, Modal, Form, Input, Switch,
  Space, Typography, Popconfirm, message,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../api';

function ConfigSection({ title, apiBase }) {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modalOpen, setModal]   = useState(false);
  const [editing, setEditing]   = useState(null);
  const [saving, setSaving]     = useState(false);
  const [form]                  = Form.useForm();

  const load = useCallback(() => {
    setLoading(true);
    api.get(apiBase)
      .then(r => { setRows(Array.isArray(r.data) ? r.data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [apiBase]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldValue('IsActive', true);
    setModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({ Name: row.Name, IsActive: !!row.IsActive });
    setModal(true);
  };

  const handleSave = async () => {
    let vals;
    try { vals = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      if (editing) await api.put(`${apiBase}/${editing.ID}`, vals);
      else         await api.post(apiBase, vals);
      message.success(editing ? 'Updated.' : 'Created.');
      setModal(false);
      load();
    } catch {
      message.error('Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`${apiBase}/${id}`);
      message.success('Deleted.');
      load();
    } catch {
      message.error('Delete failed.');
    }
  };

  const columns = [
    { title: 'Name', dataIndex: 'Name', key: 'Name', ellipsis: true },
    { title: 'Active', dataIndex: 'IsActive', key: 'IsActive', width: 70, align: 'center',
      render: v => v ? <Typography.Text type="success">Yes</Typography.Text> : <Typography.Text type="secondary">No</Typography.Text> },
    { title: '', key: 'actions', width: 80, align: 'right',
      render: (_, row) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          <Popconfirm title={`Delete "${row.Name}"?`} onConfirm={() => handleDelete(row.ID)} okText="Delete" okType="danger">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={title}
      size="small"
      extra={<Button size="small" type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add</Button>}
    >
      <Table
        rowKey="ID"
        dataSource={rows}
        columns={columns}
        loading={loading}
        size="small"
        pagination={false}
      />

      <Modal maskClosable={false}
        title={editing ? `Edit ${title.replace(/s$/, '')}` : `New ${title.replace(/s$/, '')}`}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModal(false)}
        confirmLoading={saving}
        okText={editing ? 'Save' : 'Create'}
        width={380}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="Name" label="Name" rules={[{ required: true, message: 'Name required' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="IsActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}

export default function CheersConfigPage() {
  return (
    <div style={{ padding: '16px 20px' }}>
      <Typography.Title level={4} style={{ marginBottom: 20 }}>Cheers for Peers — Configuration</Typography.Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <ConfigSection title="Sources" apiBase="/api/cheers/sources" />
        </Col>
        <Col xs={24} md={12}>
          <ConfigSection title="Categories" apiBase="/api/cheers/categories" />
        </Col>
      </Row>
    </div>
  );
}
